import { container } from 'inversify.config';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { Dimmer, Form, Loader } from 'semantic-ui-react';
import { IdentityService, Roles } from 'services/identity-service';
import { Query } from 'stores/dataStore';
import {
  ChangeEventCheckListRoleUserStore,
  eventCheckListRoleUserCheck,
  eventCheckListRoleUserDto,
  EventCheckListRoleUserStore,
  NewEventCheckListRoleUserStore
} from 'stores/events/event-checkList-roleuser-store';
import { EventFormStore, EventFormViewModel } from 'stores/events/event-form-store';
import TailwindLocationEditorWithRoleLocations from 'widgets/bussiness/location-editor-with-rolelocations-tw';

interface EventCheckListContainerProps {
  selectedEventFormViewModel: EventFormViewModel;
  eventFormStore?: EventFormStore;
  eventchecklistsroleuser?: EventCheckListRoleUserStore;
  neweventchecklistsroleuser?: NewEventCheckListRoleUserStore;
  changeeventchecklistsroleuser?: ChangeEventCheckListRoleUserStore;
  readOnly?: boolean;
}

const EventCheckListContainer: React.FC<EventCheckListContainerProps> = ({
  selectedEventFormViewModel,
  eventFormStore,
  eventchecklistsroleuser,
  neweventchecklistsroleuser,
  changeeventchecklistsroleuser,
  readOnly
}) => {
  const { t } = useTranslation();
  const identityService: IdentityService = container.get(IdentityService);
  const currentUserInfo = identityService.getUserInfo();

  const getEventRelatedLocationIds = useCallback(() => {
    //const event = eventFormStore.state.item;
    const event = selectedEventFormViewModel;
    let result = [];
    if (event && event.eventDetails && event.eventDetails.location) {
      result.push(event.eventDetails.location.id);
    }
    if (event && event.requests.length > 0) {
      event.requests.forEach(req => result.push(req.requestingLocationId));
    }
    return result;
  }, [selectedEventFormViewModel]);

  const [hasPocManyLocations, setHasPocManyLocations] = useState(false);
  const [roleLocation, setRoleLocation] = useState('');

  const getInitialQuery = () => {
    //const event = eventFormStore.state.item;
    const event = selectedEventFormViewModel;
    let q: Query = { searchQuery: '', orderBy: [], skip: 0, take: 10 };
    let searchQuery: string = '';
    if (IdentityService.isPoc(currentUserInfo)) {
      const pocLocations = currentUserInfo.locationsByRoles[currentUserInfo.activeRole] as string[];
      let eventRelatedLocationIds = getEventRelatedLocationIds();
      let locations = pocLocations.filter(x => eventRelatedLocationIds.includes(x));
      searchQuery = "role eq 'PoC' and LocationId eq " + locations[0] + '  and EventId eq ' + event.id;
      //   setHasPocManyLocations(locations.length > 1);
      //   setRoleLocation(locations[0]);
    }
    if (IdentityService.isInstructor(currentUserInfo)) {
      searchQuery = "role eq 'Instructor' and UserId eq " + currentUserInfo.userId + ' and EventId eq ' + event.id;
    }
    if (IdentityService.isPlannerTFT(currentUserInfo)) {
      searchQuery = "role eq 'Planner' and EventId eq " + event.id;
    }
    if (IdentityService.isPlannerMTC(currentUserInfo)) {
      searchQuery = "role eq 'Planner MTC' and EventId eq " + event.id;
    }
    if (IdentityService.isWorker(currentUserInfo)) {
      searchQuery = "role eq 'Employee' and UserId eq " + currentUserInfo.userId + ' and EventId eq ' + event.id;
    }
    if (IdentityService.isAdmin(currentUserInfo)) {
      searchQuery = 'EventId eq ' + event.id;
    }

    const filter = searchQuery;

    return Object.assign(q, { filter, skip: 0 });
  };

  const [query, setQuery] = useState<Query>(getInitialQuery());
  const [isUpdate, setIsUpdate] = useState(false);
  const [showSaveCheckListButton, setShowSaveCheckListButton] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const checkData = (items: eventCheckListRoleUserDto[]) => {
      const currentUser = identityService.getUserInfo();
      const event = selectedEventFormViewModel;

      if (eventchecklistsroleuser.state.items && items.length > 0) {
        changeeventchecklistsroleuser.change({
          id: items[0].id,
          eventId: items[0].eventId,
          userId: IdentityService.isPoc(currentUser) ? '' : currentUser.userId,
          locationId: items[0].locationId,
          role: currentUser.activeRole,
          roleUserChecks: items[0].roleUserChecks
        });
        setIsUpdate(true);
      } else {
        let checks: eventCheckListRoleUserCheck[] = [];
        const filterByRole = event.checkLists.filter(c => {
          if (IdentityService.isAdmin(currentUser)) return c.checkListConfig.rolesThatCreateCheckLists;
          else return c.checkListConfig.rolesThatCreateCheckLists.includes(currentUser.activeRole);
        });

        filterByRole.forEach(c => {
          checks.push({ description: c.description, checked: false });
        });

        let hasPocManyLocations: boolean = false;
        let roleLocation: string = '';
        if (IdentityService.isPoc(identityService.getUserInfo())) {
          const pocLocations = currentUser.locationsByRoles[currentUser.activeRole] as string[];
          let eventRelatedLocationIds = getEventRelatedLocationIds();
          let locations = pocLocations.filter(x => eventRelatedLocationIds.includes(x));

          hasPocManyLocations = locations.length > 1;
          roleLocation = locations[0];
        }

        neweventchecklistsroleuser.createNew({
          eventId: event.id,
          userId: IdentityService.isPoc(currentUser) || IdentityService.isPlanner(currentUser) ? '' : currentUser.userId,
          locationId: roleLocation,
          role: currentUser.activeRole,
          roleUserChecks: checks
        });

        setIsUpdate(false);
        setHasPocManyLocations(hasPocManyLocations);
        setRoleLocation(roleLocation);
      }
    };

    setLoading(true);
    const result = await eventchecklistsroleuser.getAllAsync(query);
    checkData(result.items);
    setLoading(false);
  }, [
    eventchecklistsroleuser,
    query,
    neweventchecklistsroleuser,
    changeeventchecklistsroleuser,
    selectedEventFormViewModel,
    identityService,
    getEventRelatedLocationIds
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedFromChecksList = (index: number) => {
    if (isUpdate) {
      const item = changeeventchecklistsroleuser.state.item;
      const check = item.roleUserChecks[index];
      check.checked = !check.checked;
      changeeventchecklistsroleuser.change(item);
    } else {
      const item = neweventchecklistsroleuser.state.item;
      const check = item.roleUserChecks[index];
      check.checked = !check.checked;
      neweventchecklistsroleuser.change(item);
    }
    setShowSaveCheckListButton(true);
  };

  const changePocLocationFilter = () => {
    //const event = eventFormStore.state.item;
    const event = selectedEventFormViewModel;
    let filter = '';

    if (roleLocation) {
      const searchQuery: string = `role eq 'PoC' and LocationId eq ${roleLocation} and EventId eq ${event.id}`;
      filter = searchQuery;

      const queryCopy = Object.assign(query, { filter, skip: 0 });
      setQuery(queryCopy);
    }
  };

  const isCheckEditable = () => {
    //const event = eventFormStore.state.item;
    const event = selectedEventFormViewModel;
    const status = event.status.toString();
    if (
      (status === 'Completed' || status === 'InProgress') &&
      (identityService.isInRole(Roles.Instructor) || identityService.isInRole(Roles.PoC) || event.eventCreatorId === currentUserInfo.userId)
    ) {
      return false;
    }
    return true;
  };

  const onSaveCheckList = () => {
    setLoading(true);
    if (isUpdate) {
      changeeventchecklistsroleuser.update().then(() => setLoading(false));
      changeeventchecklistsroleuser.clear();
    } else {
      neweventchecklistsroleuser.submit().then(() => setLoading(false));
      neweventchecklistsroleuser.clear();
    }
  };

  const renderCheckbox = (check: eventCheckListRoleUserCheck, index: number) => (
    <div className="flex items-center gap-2">
      <input
        id={`checkbox_${index}`}
        checked={check.checked}
        type="checkbox"
        onChange={() => selectedFromChecksList(index)}
        disabled={isCheckEditable()}
        className="w-4 h-4 text-black-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
      />
      <label htmlFor={`checkbox_${index}`} className="text-sm text-gray-700 cursor-pointer">
        {check.description}
      </label>
    </div>
  );

  return (
    <div className="space-y-4">
      <Dimmer
        active={loading || eventchecklistsroleuser.state.isBusy || eventchecklistsroleuser.state.isBusy}
        style={{ zIndex: 999, background: 'rgba(0, 0, 0, 0.4)' }}
      >
        <Loader indeterminate>{t('Loading')}</Loader>
      </Dimmer>

      {hasPocManyLocations && (
        <div>
          <label className="text-sm text-gray-700">{t('Role Locations')}</label>
          <input className="w-full border border-gray-300 p-2 rounded bg-gray-100" disabled />

          <TailwindLocationEditorWithRoleLocations
            value={roleLocation}
            locationsReceived={currentUserInfo?.locationsByRoles[currentUserInfo?.activeRole]?.toArray<string>()}
            onChange={() => changePocLocationFilter()}
          />
        </div>
      )}

      {isUpdate &&
        changeeventchecklistsroleuser.state.item &&
        changeeventchecklistsroleuser.state.item.roleUserChecks.map((c, i) => <Form.Field key={i}>{renderCheckbox(c, i)}</Form.Field>)}

      {!isUpdate &&
        neweventchecklistsroleuser.state.item &&
        neweventchecklistsroleuser.state.item.roleUserChecks.map((c, i) => <Form.Field key={i}>{renderCheckbox(c, i)}</Form.Field>)}

      {showSaveCheckListButton && (
        <button className="w-full bg-primary-600 text-white py-2 rounded mt-2" onClick={onSaveCheckList}>
          {t('Save Check List')}
        </button>
      )}
    </div>
  );
};

export default connect(
  ['eventFormStore', EventFormStore],
  ['eventchecklistsroleuser', EventCheckListRoleUserStore],
  ['neweventchecklistsroleuser', NewEventCheckListRoleUserStore],
  ['changeeventchecklistsroleuser', ChangeEventCheckListRoleUserStore]
)(EventCheckListContainer);
